import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  CircleCheckBig,
  CircleDollarSign,
  Clock3,
  CreditCard,
  RefreshCw,
  ReceiptText,
  TriangleAlert,
} from "lucide-react"

import { StaffPagePending } from "@/components/loading-skeleton"
import { formatCurrency, formatTimestamp } from "@/lib/date"
import { staffPaymentsQueryOptions } from "@/lib/query-options"

export const Route = createFileRoute("/staff/payments")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(staffPaymentsQueryOptions()),
  head: () => ({
    meta: [{ title: "Payments · Groundline staff" }],
  }),
  pendingComponent: () => <StaffPagePending kind="dashboard" />,
  component: PaymentsLedger,
})

function PaymentsLedger() {
  const { data } = useSuspenseQuery(staffPaymentsQueryOptions())
  const currency = data.payments.at(0)?.currency ?? "PHP"

  return (
    <>
      <header className="staff-page-head">
        <div>
          <span className="eyebrow">
            <CreditCard className="size-4" />
            Stripe ledger
          </span>
          <h1 className="staff-page-title">Payments.</h1>
          <p className="staff-page-copy">
            Booking charges, pending Checkout holds, and recurring Player Pass
            billing in one operational view.
          </p>
        </div>
      </header>

      <section className="metric-grid" aria-label="Payment metrics">
        <Metric
          label="Collected"
          value={formatCurrency(data.metrics.paidTotal, currency)}
          icon={CircleDollarSign}
        />
        <Metric
          label="Paid charges"
          value={String(data.metrics.paidCount)}
          icon={CircleCheckBig}
        />
        <Metric
          label="Pending holds"
          value={String(data.metrics.pendingCount)}
          icon={Clock3}
        />
        <Metric
          label="Active passes"
          value={String(data.metrics.activePasses)}
          icon={RefreshCw}
        />
      </section>

      <section className="ruled-panel mt-7 overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">
              <ReceiptText className="size-3.5" />
              Charge activity
            </span>
            <p className="mt-1 text-xs text-muted-foreground">
              {data.payments.length} recent payment records ·{" "}
              {data.metrics.failedCount} failed
            </p>
          </div>
        </div>
        {data.payments.length === 0 ? (
          <div className="empty-state">
            <div>
              <CreditCard className="size-6" />
              <strong>No payment activity</strong>
              <p className="mt-2">
                Public Stripe checkouts will appear here immediately.
              </p>
            </div>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Reference</th>
                  <th>Flow</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>
                      <strong>{payment.customerName}</strong>
                    </td>
                    <td>
                      <span className="font-semibold">
                        {payment.bookingReference ??
                          payment.stripeInvoiceId ??
                          "Subscription"}
                      </span>
                    </td>
                    <td className="capitalize">{payment.kind}</td>
                    <td>
                      <strong>
                        {formatCurrency(payment.amountCents, payment.currency)}
                      </strong>
                    </td>
                    <td>
                      <PaymentStatus status={payment.status} />
                    </td>
                    <td>{formatTimestamp(payment.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="ruled-panel mt-7 overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">
              <RefreshCw className="size-3.5" />
              Player Pass subscriptions
            </span>
            <p className="mt-1 text-xs text-muted-foreground">
              Account-linked recurring status from Stripe
            </p>
          </div>
        </div>
        {data.subscriptions.length === 0 ? (
          <div className="empty-state">
            <div>
              <RefreshCw className="size-6" />
              <strong>No Player Pass subscribers</strong>
              <p className="mt-2">
                The golfer demo account can start a test subscription.
              </p>
            </div>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Plan</th>
                  <th>Monthly</th>
                  <th>Status</th>
                  <th>Current period</th>
                </tr>
              </thead>
              <tbody>
                {data.subscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td>
                      <strong className="block">
                        {subscription.customerName}
                      </strong>
                      <small className="mt-1 block text-muted-foreground">
                        {subscription.customerEmail}
                      </small>
                    </td>
                    <td>Player Pass</td>
                    <td>
                      <strong>
                        {formatCurrency(
                          subscription.amountCents,
                          subscription.currency
                        )}
                      </strong>
                    </td>
                    <td>
                      <PaymentStatus status={subscription.status} />
                    </td>
                    <td>
                      {subscription.currentPeriodEnd
                        ? formatTimestamp(subscription.currentPeriodEnd)
                        : "Awaiting checkout"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

function Metric({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string
  icon: typeof CreditCard
}) {
  return (
    <div className="metric">
      <span className="metric__label">
        {label}
        <Icon />
      </span>
      <strong className="metric__value">{value}</strong>
    </div>
  )
}

function PaymentStatus({ status }: { status: string }) {
  const positive = status === "paid" || status === "active"
  const negative =
    status === "failed" || status === "cancelled" || status === "unpaid"
  const Icon = positive ? CircleCheckBig : negative ? TriangleAlert : Clock3

  return (
    <span
      className={`status-chip ${
        positive
          ? "status-chip--confirmed"
          : negative
            ? "status-chip--cancelled"
            : "status-chip--pending_payment"
      }`}
    >
      <Icon className="size-3" />
      {status.replaceAll("_", " ")}
    </span>
  )
}
